import { useEffect, useState } from 'react';
import { useAppDispatch } from 'src/store/hooks';
import { hasRequiredSpaceInBytes, requiredForIndexedDbInMB } from '../services/CordovaService/StorageService';
import { connectToDB } from '../store/globalStateSlice';
import PrimaryButton from 'src/components/atoms/PrimaryButton';
import { PROTOCOL, SRV_ADDR } from 'src/services/DBService/DbService';
import { getStoredLogin, saveStoredLogin } from 'src/services/AuthStorageService';

type Properties = {
    error?: string;
};

export default function Login(props: Properties) {
    const [autologin, setAutologin] = useState<boolean>(false);
    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
    const [registerUsername, setRegisterUsername] = useState<string>('');
    const [registerToken, setRegisterToken] = useState<string>('');
    const [registerPassword, setRegisterPassword] = useState<string>('');
    const [registerPasswordConfirm, setRegisterPasswordConfirm] = useState<string>('');
    const [registerTokenValidated, setRegisterTokenValidated] = useState<boolean>(false);
    const [registerBusy, setRegisterBusy] = useState<boolean>(false);
    const [registerError, setRegisterError] = useState<string>('');
    const [storageWarning, setStorageWarning] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const registerApiBase = SRV_ADDR.startsWith('localhost')
        ? `${PROTOCOL}://localhost:3001/api`
        : `${PROTOCOL}://${SRV_ADDR.replace(/\/couchdb$/, '/api')}`;
    useEffect(() => {
        const storedLogin = getStoredLogin();
        const loginDataExists = storedLogin !== undefined;
        setAutologin(loginDataExists);
        if (loginDataExists) {
            dispatch(connectToDB(storedLogin));
        }
    }, []);

    useEffect(() => {
        const checkStorage = async () => {
            if (!(await hasRequiredSpaceInBytes(requiredForIndexedDbInMB * Math.pow(10, 6)))) {
                setStorageWarning(true);
            }
        };
        checkStorage();
    }, []);

    const login = () => {
        try {
            saveStoredLogin(username, password);
            dispatch(connectToDB({ username, password }));
        } catch (error) {
            console.error(error);
        }
        
    };

    const validateRegisterToken = async () => {
        const trimmedUsername = registerUsername.trim();
        const trimmedToken = registerToken.trim();
        if (!trimmedUsername || !trimmedToken) {
            setRegisterError('Bitte Benutzername und Token eingeben.');
            return;
        }

        setRegisterBusy(true);
        setRegisterError('');
        try {
            const res = await fetch(`${registerApiBase}/account/register_with_token/validate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: trimmedUsername, token: trimmedToken }),
            });
            const payload = await res.json().catch(() => undefined);
            if (!res.ok) {
                setRegisterError(payload?.error || 'Token-Prüfung fehlgeschlagen.');
                return;
            }
            setRegisterTokenValidated(true);
        } catch (error: any) {
            setRegisterError(error?.message || 'Token-Prüfung fehlgeschlagen.');
        } finally {
            setRegisterBusy(false);
        }
    };

    const completeRegistration = async () => {
        const trimmedUsername = registerUsername.trim();
        const trimmedToken = registerToken.trim();
        if (!registerTokenValidated) {
            setRegisterError('Bitte Token zuerst prüfen.');
            return;
        }
        if (!registerPassword || !registerPasswordConfirm) {
            setRegisterError('Bitte Passwort und Passwort-Bestätigung eingeben.');
            return;
        }
        if (registerPassword !== registerPasswordConfirm) {
            setRegisterError('Passwörter stimmen nicht überein.');
            return;
        }
        if (registerPassword.length < 8) {
            setRegisterError('Passwort muss mindestens 8 Zeichen lang sein.');
            return;
        }

        setRegisterBusy(true);
        setRegisterError('');
        try {
            const res = await fetch(`${registerApiBase}/account/register_with_token/complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: trimmedUsername, token: trimmedToken, newPassword: registerPassword }),
            });
            const payload = await res.json().catch(() => undefined);
            if (!res.ok) {
                setRegisterError(payload?.error || 'Registrierung fehlgeschlagen.');
                return;
            }

            saveStoredLogin(trimmedUsername, registerPassword);
            dispatch(connectToDB({ username: trimmedUsername, password: registerPassword }));
        } catch (error: any) {
            setRegisterError(error?.message || 'Registrierung fehlgeschlagen.');
        } finally {
            setRegisterBusy(false);
        }
    };

    if (autologin) {
        return <div>automatic login</div>;
    }

    let error = props.error;
    if (storageWarning) {
        error = `Speicherwarnung! Die App benötigt mindestens ${requiredForIndexedDbInMB} MB freien Speicher, um die Inhalte zu speichern.`;
    }

    return (
        <form action="" onSubmit={ev => ev.preventDefault()}>
            <div className="login-form">
                {authMode === 'login' && (
                    <>
                        <div className="login-panel-header">
                            <h2>Anmelden</h2>
                            <p>Mit deinem Code und Passwort anmelden.</p>
                        </div>
                        <fieldset>
                            <label htmlFor="username">Code</label>
                            <input
                                type="text"
                                id="username"
                                value={username}
                                onChange={ev => setUsername(ev.target.value)}
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck={false}
                            />
                        </fieldset>
                        <fieldset>
                            <label htmlFor="password">Passwort</label>
                            <input type="password" id="password" value={password} onChange={ev => setPassword(ev.target.value)} />
                            {error !== undefined ? <p className="error">{error}</p> : undefined}
                        </fieldset>
                        <p>
                            Wenn Du auf "Einloggen" klickst, dann bestätigst du, dass du die Datenschutzinformationen zur Studie erhalten und gelesen hast und mit allen aufgelisteten Punkten einverstanden bist. Insbesondere der Erhebung, Speicherung, Verarbeitung und Verknüpfung deiner Daten in den Befragungen der Studie.
                        </p>
                        <PrimaryButton
                            type="button"
                            onClick={login}
                            disabled={storageWarning}
                            label='Einloggen'
                        />
                        <div className="login-secondary-action">
                            <span>Noch kein Passwort?</span>
                            <button
                                type="button"
                                className="btn btn-link p-0"
                                onClick={() => {
                                    setAuthMode('register');
                                    setRegisterError('');
                                }}
                            >
                                Mit Token registrieren
                            </button>
                        </div>
                    </>
                )}

                {authMode === 'register' && (
                    <>
                        <div className="login-panel-header">
                            <h2>Registrieren</h2>
                            <p>Benutzername und Token eingeben, danach Passwort setzen.</p>
                        </div>
                        <fieldset>
                            <label htmlFor="register-username">Code</label>
                            <input
                                type="text"
                                id="register-username"
                                value={registerUsername}
                                onChange={ev => setRegisterUsername(ev.target.value)}
                                disabled={registerTokenValidated}
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck={false}
                            />
                        </fieldset>
                        <fieldset>
                            <label htmlFor="register-token">Registrierungs-Token</label>
                            <input
                                type="text"
                                id="register-token"
                                value={registerToken}
                                onChange={ev => setRegisterToken(ev.target.value)}
                                disabled={registerTokenValidated}
                            />
                        </fieldset>

                        {!registerTokenValidated && (
                            <PrimaryButton
                                type="button"
                                onClick={() => { void validateRegisterToken(); }}
                                disabled={registerBusy || storageWarning}
                                label={registerBusy ? 'Prüfen…' : 'Token prüfen'}
                            />
                        )}

                        {registerTokenValidated && (
                            <>
                                <fieldset>
                                    <label htmlFor="register-password">Passwort</label>
                                    <input
                                        type="password"
                                        id="register-password"
                                        value={registerPassword}
                                        onChange={ev => setRegisterPassword(ev.target.value)}
                                    />
                                </fieldset>
                                <fieldset>
                                    <label htmlFor="register-password-confirm">Passwort bestätigen</label>
                                    <input
                                        type="password"
                                        id="register-password-confirm"
                                        value={registerPasswordConfirm}
                                        onChange={ev => setRegisterPasswordConfirm(ev.target.value)}
                                    />
                                </fieldset>
                                <PrimaryButton
                                    type="button"
                                    onClick={() => { void completeRegistration(); }}
                                    disabled={registerBusy || storageWarning}
                                    label={registerBusy ? 'Registrieren…' : 'Registrierung abschließen'}
                                />
                            </>
                        )}

                        {registerError !== undefined && registerError !== '' ? <p className="error">{registerError}</p> : undefined}
                        <div className="login-secondary-action">
                            <button
                                type="button"
                                className="btn btn-link p-0"
                                onClick={() => {
                                    setAuthMode('login');
                                    setRegisterError('');
                                }}
                            >
                                Zurück zum Login
                            </button>
                        </div>
                    </>
                )}
            </div>
        </form>
    );
}
